import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faTwitter, faLinkedin, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faLink } from '@fortawesome/free-solid-svg-icons';
import Navbar from './Navbar';
import Footer from './Footer';
import { getWebAppUrl } from '../utils/config';
import { DEFAULT_TRIAL_PERIOD_DAYS } from '../config/config';
import { getMarkdownPost, isMarkdownPost } from '../content/blog';

// Re-export for use in BlogPost.tsx
export { isMarkdownPost };

const MarkdownBlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getMarkdownPost(slug) : null;

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';
  const shareText = `Check out this article: ${shareTitle}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
  };

  if (!post) {
    return null; // Let BlogPost handle the 404
  }

  return (
    <>
      <Navbar />
      <Container className="py-5 mt-5">
        <Row>
          <Col lg={8} className="mx-auto">
            <div className="mb-4">
              <Link to="/blog" className="text-decoration-none">
                ← Back to Blog
              </Link>
            </div>

            <article>
              <header className="mb-5">
                <h1 className="display-4 fw-bold mb-3">{post.title}</h1>
                <div className="text-muted">
                  <span>{post.date}</span>
                  <span className="ms-3">·</span>
                  <span className="ms-3">{post.readTime}</span>
                </div>
              </header>

              <div className="blog-content markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.content}
                </ReactMarkdown>
              </div>

              <div className="text-center py-5">
                <h3 className="mb-4">Ready to Automate Your Marketing?</h3>
                <p className="lead mb-4">
                  The Defined CRM includes automated postcard marketing to help you reach more customers effortlessly.
                </p>
                <a href={`${getWebAppUrl()}/create-account`} className="btn btn-primary btn-lg me-3">
                  {DEFAULT_TRIAL_PERIOD_DAYS > 0 ? `Start Your ${DEFAULT_TRIAL_PERIOD_DAYS}-Day Free Trial` : 'Get Started'}
                </a>
                <Link to="/" className="btn btn-outline-primary btn-lg">
                  Learn More
                </Link>
              </div>
            </article>

            <div className="border-top border-bottom py-4 mt-5 mb-4">
              <div className="text-center">
                <h5 className="mb-3">Share this article</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-link p-0 text-primary share-icon"
                    onClick={() => window.open(shareLinks.facebook, '_blank')}
                    title="Share on Facebook"
                  >
                    <FontAwesomeIcon icon={faFacebook} size="2x" />
                  </button>
                  <button
                    className="btn btn-link p-0 text-info share-icon"
                    onClick={() => window.open(shareLinks.twitter, '_blank')}
                    title="Share on Twitter"
                  >
                    <FontAwesomeIcon icon={faTwitter} size="2x" />
                  </button>
                  <button
                    className="btn btn-link p-0 text-primary share-icon"
                    onClick={() => window.open(shareLinks.linkedin, '_blank')}
                    title="Share on LinkedIn"
                  >
                    <FontAwesomeIcon icon={faLinkedin} size="2x" />
                  </button>
                  <button
                    className="btn btn-link p-0 text-success share-icon"
                    onClick={() => window.open(shareLinks.whatsapp, '_blank')}
                    title="Share on WhatsApp"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} size="2x" />
                  </button>
                  <button
                    className="btn btn-link p-0 text-danger share-icon"
                    onClick={() => window.location.href = shareLinks.email}
                    title="Share via Email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} size="2x" />
                  </button>
                  <button
                    className="btn btn-link p-0 text-secondary share-icon"
                    onClick={handleCopyLink}
                    title="Copy Link"
                  >
                    <FontAwesomeIcon icon={faLink} size="2x" />
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-4 mt-2">
              <Link to="/blog" className="btn btn-outline-primary">
                ← Back to All Posts
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
      <Footer />
    </>
  );
};

export default MarkdownBlogPost;
